import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, Paper, Slide, useTheme as useMuiTheme, useMediaQuery, IconButton, Tooltip } from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useChat } from '../../contexts/ChatContext';
import { chatTranslations } from './chatTranslations';
import ConversationList from './ConversationList';
import MessageList from './MessageList';
import GroupManageDialog from './GroupManageDialog';
import NewDirectDialog from './NewDirectDialog';

const ChatWidget: React.FC = () => {
  const { isOpen, activeConversationId, conversations, setActiveConversation } = useChat();
  const muiTheme = useMuiTheme();
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const isDark = muiTheme.palette.mode === 'dark';
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [newDirectOpen, setNewDirectOpen] = useState(false);
  const [groupSettingsOpen, setGroupSettingsOpen] = useState(false);

  // Responsive breakpoints
  const isMobile = useMediaQuery('(max-width:599px)');
  const isTablet = useMediaQuery('(min-width:600px) and (max-width:959px)');

  const activeConversation = conversations.find(c => c.conversationId === activeConversationId) ?? null;

  // On mobile: show either conversation list OR messages (not both)
  const showConvList = isMobile ? !activeConversationId : true;
  const showMessages = isMobile ? !!activeConversationId : true;

  return ReactDOM.createPortal(
    <>
      <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            bottom: { xs: 0, sm: 8, md: 16 },
            [isRTL ? 'left' : 'right']: { xs: 0, sm: 8, md: 16 },
            width: { xs: '100vw', sm: 420, md: 780 },
            height: { xs: '100dvh', sm: 'min(520px, calc(100dvh - 16px))', md: 'min(520px, calc(100dvh - 80px))' },
            display: 'flex',
            overflow: 'hidden',
            borderRadius: { xs: 0, sm: 3 },
            zIndex: 1500,
            background: isDark
              ? 'linear-gradient(180deg, #1e2a4a 0%, #151c32 100%)'
              : '#ffffff',
            border: {
              xs: 'none',
              sm: isDark
                ? '1px solid rgba(79,172,254,0.25)'
                : '1px solid rgba(79,172,254,0.3)',
            },
            boxShadow: isDark
              ? '0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(79,172,254,0.1), inset 0 1px 0 rgba(79,172,254,0.08)'
              : '0 8px 40px rgba(79,172,254,0.2), 0 0 0 1px rgba(79,172,254,0.08)',
          }}
        >
          {/* Conversation list panel */}
          {showConvList && (
            <Box sx={{
              width: { xs: '100%', sm: 220, md: 260 },
              flexShrink: 0,
              borderRight: isRTL ? 'none' : `1px solid ${isDark ? 'rgba(79,172,254,0.12)' : 'rgba(0,0,0,0.08)'}`,
              borderLeft: isRTL ? `1px solid ${isDark ? 'rgba(79,172,254,0.12)' : 'rgba(0,0,0,0.08)'}` : 'none',
              display: 'flex', flexDirection: 'column',
              order: isRTL ? 1 : 0,
              background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(248,250,252,1)',
            }}>
              <ConversationList onCreateGroup={() => setGroupDialogOpen(true)} onNewDirect={() => setNewDirectOpen(true)} />
            </Box>
          )}

          {/* Messages panel */}
          {showMessages && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', order: isRTL ? 0 : 1 }}>
              {/* Mobile back button */}
              {isMobile && activeConversation && (
                <Box sx={{
                  display: 'flex', alignItems: 'center',
                  px: 1, py: 0.5,
                  background: isDark
                    ? 'linear-gradient(135deg, #1e2a4a 0%, #2d3a5c 100%)'
                    : 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  gap: 1,
                  flexShrink: 0,
                }}>
                  <IconButton size="small" onClick={() => setActiveConversation(null)}
                    sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
                    <BackIcon sx={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />
                  </IconButton>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ color: 'white', fontWeight: 700, fontSize: '0.88rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {activeConversation.name}
                    </Box>
                  </Box>
                </Box>
              )}
              {activeConversation ? (
                <MessageList conversation={activeConversation} onOpenGroupSettings={() => setGroupSettingsOpen(true)} />
              ) : (
                <Box sx={{
                  flex: 1, display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: 'text.secondary',
                  flexDirection: 'column', gap: 1,
                }}>
                  <Box sx={{ fontSize: 38, opacity: 0.4 }}>💬</Box>
                  <Box sx={{ fontSize: 13, color: 'text.disabled' }}>
                    {chatTranslations[language].selectConversation}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Paper>
      </Slide>

      {/* All dialogs rendered OUTSIDE the Slide/Paper — no overflow clipping */}
      <NewDirectDialog open={newDirectOpen} onClose={() => setNewDirectOpen(false)} />

      <GroupManageDialog
        open={groupDialogOpen}
        onClose={() => setGroupDialogOpen(false)}
        mode="create"
      />

      {activeConversation && (
        <GroupManageDialog
          open={groupSettingsOpen}
          onClose={() => setGroupSettingsOpen(false)}
          conversationId={activeConversation.conversationId}
          existingMembers={activeConversation.members}
          mode="manage"
        />
      )}
    </>,
    document.body
  );
};

export default ChatWidget;
